import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/PrefsContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Users, Phone, FileText, Calendar, Sparkles,
  Receipt, CreditCard, Briefcase, DollarSign, Star, BarChart3, Settings, LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/clients", label: "Customers", icon: Users, key: "customers" },
  { to: "/leads", label: "Call Center", icon: Phone, key: "callcenter" },
  { to: "/estimates", label: "Estimates", icon: FileText, key: "estimates" },
  { to: "/schedule", label: "Schedule", icon: Calendar, key: "schedule" },
  { to: "/services", label: "Services", icon: Sparkles, key: "services" },
  { to: "/invoices", label: "Invoices", icon: Receipt, key: "invoices" },
  { to: "/payments", label: "Payments", icon: CreditCard, key: "payments" },
  { to: "/employees", label: "Employees", icon: Briefcase, key: "employees" },
  { to: "/payroll", label: "Payroll", icon: DollarSign, key: "payroll" },
  { to: "/reviews", label: "Reviews", icon: Star, key: "reviews" },
  { to: "/reports", label: "Reports", icon: BarChart3, key: "reports" },
  { to: "/settings", label: "Settings", icon: Settings, key: "settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInner = (
    <div className="flex flex-col h-full text-white">
      <div className="px-5 py-6 border-b border-white/10">
        <Logo size={44} showText dark />
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end, key }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${key}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-gradient-to-r from-[#F0C94A] to-[#D4AF37] text-[#3a2a00] shadow-[0_6px_16px_-6px_rgba(212,175,55,0.7)] font-semibold"
                  : "text-white/80 hover:bg-white/10 hover:text-white hover:translate-x-0.5"
              }`
            }
          >
            <Icon size={18} strokeWidth={1.9} />
            {t(label)}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2 mb-2 rounded-xl bg-white/10">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37] text-[#3a2a00] flex items-center justify-center font-bold">
            {user?.name?.[0] || "L"}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user?.name || "Lilian Villeda"}</div>
            <div className="text-xs text-white/60">{t("Administrator")}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/90 border border-white/20 hover:bg-white/10 transition-all"
        >
          <LogOut size={16} /> {t("Sign Out")}
        </button>
      </div>
    </div>
  );

  const sidebarBg = { background: "linear-gradient(180deg, #5A189A 0%, #46127C 55%, #2E0A52 100%)" };

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 shadow-[4px_0_30px_-12px_rgba(90,24,154,0.5)] no-print z-30" style={sidebarBg}>
        {SidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 no-print" style={sidebarBg}>
        <Logo size={36} showText dark />
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="p-2 text-white">
          <Menu />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 no-print">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 shadow-xl" style={sidebarBg}>
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 p-2 text-white/80">
              <X />
            </button>
            {SidebarInner}
          </div>
        </div>
      )}

      <main className="lg:ml-64 min-h-screen bg-white">
        <div className="p-5 md:p-8 max-w-[1500px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
